/**
 * HeaderSearchBar — the persistent catalogue search (STORY-003-008; restyled
 * 2026-08).
 *
 * Hosted in the minimal global header (`SearchBarLayout`) so it is visible on
 * the marketplace, chat, and account routes. Pressing Enter or clicking the
 * search button navigates to
 * `/marketplace/search?q=<encodeURIComponent(query)>` (AC #1). The query is
 * capped at `SEARCH_Q_MAX_LEN` (200) client-side because the backend 422s a
 * longer `q`.
 *
 * WHAT CHANGED VISUALLY. The field and its submit button were two separate
 * floating controls with a gap between them — a rounded input, then a
 * detached teal square. They are now ONE joined well: a recessed
 * `--g-sunken` trough on a drawn `--g-rule` border, with the submit as a
 * flush primary segment on its right edge. The whole thing takes the focus
 * ring as a unit, which is both what it is semantically (one form) and what
 * makes it read as a counter you speak across rather than as two widgets.
 *
 * Debounce: the story specs a 300ms debounce — deliberately NOT instant-search
 * (cost discipline; only a submit triggers a query). The debounce gates the
 * recent-search dropdown refresh / a future typeahead; submission itself is
 * immediate on Enter / button-click.
 *
 * Recent searches: on focus, a dropdown shows the top `RECENT_DROPDOWN_LIMIT`
 * (5) entries from `localStorage` (NOT JWT data — `localStorage` is acceptable
 * for this per 05-GUARDRAILS). Submitting records the query. The rows are drawn
 * with `g-ruled` so the dropdown is a small ledger of what you asked before.
 *
 * Responsive (AC #10): below `md` the bar collapses to a single search icon;
 * tapping it opens a fullscreen overlay with the input autofocused. The overlay
 * is a hand-rolled accessible dialog — `@radix-ui` Sheet is not a project
 * dependency, and the same pattern is used by the filter Sheet.
 *
 * A11y: the form carries `role="search"` + an `aria-label`; the input has its
 * own `aria-label`. The dropdown is a labelled list of buttons (deliberately
 * NOT a listbox — a focusable `<button>` inside `role="option"` trips axe
 * nested-interactive).
 *
 * Anti-XSS: the query is only ever bound as React text / a URL-encoded query
 * param — never `dangerouslySetInnerHTML`.
 */
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type FormEvent,
  type ReactElement,
} from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Clock, Search, X } from "lucide-react";

import { iconProps } from "@/lib/icons";
import { MOTION, slideOverVariants } from "@/lib/motion";
import { useGardenMotion } from "@/lib/use-garden-motion";
import {
  addRecentSearch,
  getRecentSearches,
  RECENT_DROPDOWN_LIMIT,
  type RecentSearch,
} from "@/lib/recent-searches";
import { SEARCH_Q_MAX_LEN } from "@/features/marketplace/useMarketplaceSearch";

/** Debounce window for the recent-search dropdown refresh (story: 300ms). */
export const SEARCH_DEBOUNCE_MS = 300;

/** Build the results-page URL with a URL-encoded query (story AC #1). */
export function searchResultsHref(query: string): string {
  return `/marketplace/search?q=${encodeURIComponent(query)}`;
}

interface SearchFieldProps {
  /** Auto-focus on mount (the mobile overlay focuses immediately — AC #10). */
  autoFocus?: boolean;
  /** Distinguish the inline desktop field from the overlay field for testids. */
  variant: "inline" | "overlay";
  onSubmitted: () => void;
}

/**
 * The shared input + dropdown. Owns its own draft state so the inline and the
 * overlay instances are independent (the overlay opens with an empty field).
 */
function SearchField({
  autoFocus,
  variant,
  onSubmitted,
}: SearchFieldProps): ReactElement {
  const { t } = useTranslation("marketplace");
  const navigate = useNavigate();
  const { reduced } = useGardenMotion();
  const [draft, setDraft] = useState<string>("");
  const [debounced, setDebounced] = useState<string>("");
  const [open, setOpen] = useState<boolean>(false);
  const [recent, setRecent] = useState<RecentSearch[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const blurTimer = useRef<number | null>(null);

  // Refresh the recent-search list when the dropdown opens.
  const refreshRecent = useCallback((): void => {
    setRecent(getRecentSearches().slice(0, RECENT_DROPDOWN_LIMIT));
  }, []);

  // Debounce the draft → `debounced` (story: 300ms). The debounced value gates
  // the dropdown refresh; submission stays immediate.
  useEffect(() => {
    const handle = window.setTimeout(() => {
      setDebounced(draft);
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      window.clearTimeout(handle);
    };
  }, [draft]);

  useEffect(() => {
    if (open) refreshRecent();
    // `debounced` is intentionally a dependency: a debounced keystroke while the
    // dropdown is open re-evaluates the recent list (a no-op today, the hook for
    // a future server typeahead — out of scope per the story).
  }, [debounced, open, refreshRecent]);

  useEffect(() => {
    return () => {
      if (blurTimer.current !== null) window.clearTimeout(blurTimer.current);
    };
  }, []);

  const submit = useCallback(
    (rawQuery: string): void => {
      const query = rawQuery.trim().slice(0, SEARCH_Q_MAX_LEN);
      // Empty query is never submitted in search mode (the backend 422s it).
      if (query.length === 0) return;
      addRecentSearch(query);
      setOpen(false);
      navigate(searchResultsHref(query));
      onSubmitted();
    },
    [navigate, onSubmitted],
  );

  const handleSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>): void => {
      event.preventDefault();
      submit(draft);
    },
    [draft, submit],
  );

  const handleFocus = useCallback((): void => {
    if (blurTimer.current !== null) {
      window.clearTimeout(blurTimer.current);
      blurTimer.current = null;
    }
    refreshRecent();
    setOpen(true);
  }, [refreshRecent]);

  const handleBlur = useCallback((): void => {
    // Defer close so a mousedown on a dropdown row registers before unmount.
    blurTimer.current = window.setTimeout(() => {
      setOpen(false);
    }, 120);
  }, []);

  const handleRecentPick = useCallback(
    (q: string): void => {
      setDraft(q);
      submit(q);
    },
    [submit],
  );

  const showDropdown = open && recent.length > 0;

  return (
    <form
      role="search"
      aria-label={t("search.formLabel")}
      onSubmit={handleSubmit}
      className="relative w-full"
      data-testid={`header-search-form-${variant}`}
    >
      {/* One joined well: trough + submit segment take the ring as a unit. */}
      <div className="flex h-11 w-full items-stretch overflow-hidden rounded-[10px] border border-[color:var(--g-rule)] bg-[color:var(--g-sunken)] transition-colors focus-within:border-[color:var(--g-primary)] focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 focus-within:ring-offset-background">
        <span className="flex shrink-0 items-center pl-3 text-[color:var(--g-ink-2)]">
          <Search {...iconProps("sm")} />
        </span>
        <input
          // AC #10: the mobile overlay opens with the input focused; the inline
          // desktop field never auto-focuses (autoFocus defaults to undefined).
          autoFocus={autoFocus}
          ref={inputRef}
          type="search"
          inputMode="search"
          enterKeyHint="search"
          maxLength={SEARCH_Q_MAX_LEN}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onFocus={handleFocus}
          onBlur={handleBlur}
          aria-label={t("search.inputLabel")}
          placeholder={t("search.placeholder")}
          data-testid={`header-search-input-${variant}`}
          className="min-w-0 flex-1 bg-transparent px-2.5 text-sm text-foreground placeholder:text-[color:var(--g-ink-2)] focus:outline-none [&::-webkit-search-cancel-button]:appearance-none"
        />
        <button
          type="submit"
          aria-label={t("search.submit")}
          data-testid={`header-search-submit-${variant}`}
          className="flex w-12 shrink-0 items-center justify-center bg-[color:var(--g-primary)] text-[color:var(--g-on-primary)] transition-opacity hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[color:var(--g-on-primary)]"
        >
          <Search {...iconProps("md")} />
        </button>
      </div>

      {/* A ledger of previous questions, not a combobox. The fade+y entrance
          mirrors the house style; exit runs fast and is hard-removed under
          reduced motion so the blur-close stays prompt. */}
      <AnimatePresence>
        {showDropdown && (
          <motion.ul
            aria-label={t("search.recentLabel")}
            data-testid={`header-search-recent-${variant}`}
            className="g-ruled absolute left-0 right-0 top-[calc(100%+6px)] z-50 m-0 max-h-72 list-none overflow-auto rounded-[10px] border border-[color:var(--g-rule)] bg-popover p-0 text-popover-foreground shadow-[var(--g-shadow-lg)]"
            initial={reduced ? false : { opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={reduced ? { opacity: 0 } : { opacity: 0, y: -4 }}
            transition={{
              duration: reduced ? 0 : MOTION.dur.nav,
              ease: MOTION.ease.out,
            }}
          >
            {recent.map((entry) => (
              <li key={`${entry.q}-${entry.when}`}>
                <button
                  type="button"
                  // mousedown fires before the input's blur — pick before close.
                  onMouseDown={(e) => {
                    e.preventDefault();
                    handleRecentPick(entry.q);
                  }}
                  data-testid={`header-search-recent-item-${variant}`}
                  className="flex min-h-[44px] w-full items-center gap-2.5 px-3 text-left text-sm text-foreground transition-colors hover:bg-[color:var(--g-sunken)] focus-visible:bg-[color:var(--g-sunken)] focus-visible:outline-none"
                >
                  <Clock
                    {...iconProps("sm")}
                    className="shrink-0 text-[color:var(--g-ink-2)]"
                  />
                  <span className="truncate">{entry.q}</span>
                </button>
              </li>
            ))}
          </motion.ul>
        )}
      </AnimatePresence>
    </form>
  );
}

export function HeaderSearchBar(): ReactElement {
  const { t } = useTranslation("marketplace");
  const { reduced, overlay } = useGardenMotion();
  const [overlayOpen, setOverlayOpen] = useState<boolean>(false);
  const closeOverlay = useCallback((): void => setOverlayOpen(false), []);

  // Close the overlay on Escape (story: fullscreen overlay open/close — AC #10).
  useEffect(() => {
    if (!overlayOpen) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") setOverlayOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [overlayOpen]);

  return (
    <div className="flex w-full items-center" data-testid="header-search-bar">
      {/* Inline bar at ≥ lg (1024px). Below lg the icon takes over: at md
          (768px) the tabs + this bar + the tools measured 849px wide — the
          input's intrinsic min-content keeps the bar from shrinking — which
          pushed the account menu off-screen (responsive pass, 2026-10-15). */}
      <div className="hidden w-full max-w-md lg:block" data-testid="header-search-inline">
        <SearchField variant="inline" onSubmitted={closeOverlay} />
      </div>

      {/* Phones and tablets (< lg): a search icon that opens the fullscreen
          overlay (AC #10). */}
      <button
        type="button"
        className="inline-flex size-11 items-center justify-center rounded-[10px] text-foreground transition-colors hover:bg-[color:var(--g-sunken)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background lg:hidden"
        aria-label={t("search.openOverlay")}
        aria-haspopup="dialog"
        aria-expanded={overlayOpen}
        onClick={() => setOverlayOpen(true)}
        data-testid="header-search-open"
      >
        <Search {...iconProps("lg")} />
      </button>

      {/* Fullscreen overlay (< lg). Hand-rolled accessible dialog — no extra
          dependency (consistent with MarketplacePage's filter Sheet). The
          panel slides up on enter and exits fast; under reduced motion it shows
          and hard-removes instantly so the autofocus + Escape close stay prompt.
          The input mounts synchronously (Framer renders children immediately),
          so the `toBeFocused()` contract holds. */}
      <AnimatePresence>
        {overlayOpen && (
          <motion.div
            className="fixed inset-0 z-50 flex flex-col bg-background lg:hidden"
            role="dialog"
            aria-modal="true"
            aria-label={t("search.overlayLabel")}
            data-testid="header-search-overlay"
            variants={reduced ? undefined : slideOverVariants("y", 8)}
            {...overlay()}
          >
            <div className="flex items-center gap-2 border-b border-[color:var(--g-rule-strong)] p-3">
              <div className="min-w-0 flex-1">
                <SearchField variant="overlay" autoFocus onSubmitted={closeOverlay} />
              </div>
              <button
                type="button"
                aria-label={t("search.closeOverlay")}
                onClick={closeOverlay}
                data-testid="header-search-overlay-close"
                className="inline-flex size-11 shrink-0 items-center justify-center rounded-[10px] text-[color:var(--g-ink-2)] transition-colors hover:bg-[color:var(--g-sunken)] hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
              >
                <X {...iconProps("lg")} />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default HeaderSearchBar;
